'use client';

import {
  createContext,
  use,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuthToken, getAuthToken, getLoginUrl, getDiscordLoginUrl, setAuthRedirect } from './auth-token';
import { apiPost } from './api';
import { authKeys, useAuthQuery } from '@/hooks/api/useAuth';
import type { AuthUser } from '@/types/auth';

export type { AuthUser } from '@/types/auth';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  loginWithGoogle: (redirectTo?: string) => void;
  loginWithDiscord: (redirectTo?: string) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const loginWithGoogle = (redirectTo = '/dashboard') => {
  setAuthRedirect(redirectTo);
  window.location.href = getLoginUrl();
};

const loginWithDiscord = (redirectTo = '/dashboard') => {
  setAuthRedirect(redirectTo);
  window.location.href = getDiscordLoginUrl();
};

export const useAuth = () => {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within SessionProvider');
  return ctx;
};

/** @deprecated Use useAuth instead */
export const useSession = () => {
  const { user, isLoading, signOut, isAuthenticated } = useAuth();
  return {
    session: user
      ? {
          user: {
            id: user.id,
            email: user.email,
            name: user.name,
            avatar_url: user.avatar_url,
            is_super_admin: user.is_super_admin,
            user_metadata: {
              full_name: user.name,
              avatar_url: user.avatar_url,
            },
          },
          access_token: getAuthToken(),
        }
      : null,
    isLoading,
    signOut,
    isAuthenticated,
  };
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { data: user = null, isLoading, isFetching, refetch } = useAuthQuery();

  const refreshUser = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: authKeys.me });
    await refetch();
  }, [queryClient, refetch]);

  const signOut = useCallback(async () => {
    try {
      await apiPost('/auth/logout');
    } catch {
      // Cookie puede no existir en dev
    }
    clearAuthToken();
    queryClient.removeQueries({ queryKey: authKeys.me });
    window.location.href = '/login';
  }, [queryClient]);

  const contextValue = useMemo(
    () => ({
      user,
      isLoading: isLoading || isFetching,
      isAuthenticated: !!user,
      signOut,
      loginWithGoogle,
      loginWithDiscord,
      refreshUser,
    }),
    [user, isLoading, isFetching, signOut, refreshUser],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
